// ============================================================================
// LE MOT D'UN CHAMP, POUR TOUTE LA SPA — « Saison », « Millésime » ou « Pitouf », selon le magasin.
//
// UTILITÉ : un seul endroit où un écran demande comment ce magasin appelle un champ. Les libellés en
// dur (« Famille », « Fournisseur »…) migrent vers `fieldLabel('family')` écran par écran, et l'audit
// `audit-libelles-en-dur` interdit d'en réécrire un.
//
// ⚠️ MÊME MÉCANIQUE QUE LA DEVISE (`setCurrentCurrency` de money-format) : un singleton de module posé
//    par le contexte magasin AVANT que la vue ne soit commitée, lu au moment du rendu. Un changement de
//    magasin recharge la page entière, donc un mot ne peut jamais rester périmé en cours de session.
// ⚠️ LU AU RENDU, JAMAIS À L'IMPORT : une constante de module qui appellerait `fieldLabel` au chargement
//    du fichier figerait le filet avant que le contexte n'arrive — l'écran dirait « Saison » chez le
//    caviste. Les tables `{ champ, label }` déclarées au niveau du module se lisent donc par une
//    fonction, pas par une valeur.
// ⚠️ LE FILET EST LÀ PAR CONSTRUCTION : tant que rien n'est posé — pas de magasin actif, contexte pas
//    encore lu — on affiche le mot de la plateforme. Jamais une clé, jamais du blanc.
// ============================================================================
import {
  platformVocabulary,
  type Vocabulary, type VocabularyFieldKey, type VocabularyLang,
} from '@orgazone/sync-contract';
import { currentLang, platformLang, setCurrentLang } from './lang';
import type { ArticleFacet } from './api';

/**
 * LA LANGUE DU COMPTE — tenue par `lang.ts`, pas ici.
 * ⚠️ Elle a DEUX lecteurs depuis que l'interface se traduit : le vocabulaire (le mot d'un champ) et
 *    l'i18n (nos textes). La garder ici aurait fait lire « la langue du vocabulaire » à un bouton
 *    « Annuler » — un nom qui ment sur ce qu'il désigne. Ces deux-ci restent pour les appelants du
 *    vocabulaire, et délèguent.
 */
export function currentVocabularyLang(): VocabularyLang {
  return currentLang();
}

/** La langue de la plateforme — celle qu'applique un compte qui n'a rien choisi (voir `lang.ts`). */
export function platformVocabularyLang(): VocabularyLang {
  return platformLang();
}

/**
 * Pose la langue du compte, au démarrage de la SPA — `null` (compte sans langue) = celle de la plateforme.
 * ⚠️ AVANT le premier rendu, et avant les mots : le filet employé tant que le contexte magasin n'est pas
 *    lu doit déjà être dans LA BONNE LANGUE, sinon l'écran affiche « Saison » puis « Temporada » pour le
 *    même champ, et personne ne sait lequel des deux il a lu (§23.0).
 * ⚠️ Une langue que le contrat ne connaît pas retombe sur celle de la plateforme, sans erreur — même
 *    règle qu'au serveur : une langue retirée du contrat ne doit pas faire tomber la page d'un compte
 *    qui la portait encore.
 */
export function setCurrentVocabularyLang(next: string | null | undefined): void {
  setCurrentLang(next);
  // Le filet suit la langue : sans cette ligne, les mots posés avant le contexte resteraient français.
  if (posed === null) current = platformVocabulary(currentLang());
}

/** Les mots du magasin tels qu'ils ont été posés — `null` tant que le contexte n'a rien dit. */
let posed: Vocabulary | null = null;
let current: Vocabulary = platformVocabulary(currentLang());

/** Pose les mots du magasin actif ; `null` = aucun magasin, on revient au filet de la plateforme. */
export function setCurrentVocabulary(vocabulary: Vocabulary | null): void {
  posed = vocabulary;
  current = vocabulary ?? platformVocabulary(currentLang());
}

/** Le vocabulaire en vigueur — pour les écrans qui ont besoin de toute la table d'un coup. */
export function currentVocabulary(): Vocabulary {
  return current;
}

/** Le mot que CE magasin emploie pour ce champ. */
export function fieldLabel(field: VocabularyFieldKey): string {
  return current[field];
}

/**
 * LA CLÉ DE VOCABULAIRE D'UNE FACETTE D'ARTICLE — la correspondance écrite UNE fois.
 * ⚠️ Trois écrans (promotions, exclusions de soldes, prix de masse) l'avaient chacun réécrite en
 *    migrant : trois tables « à peu près pareilles » divergent au premier ajout. Les facettes sont les
 *    codes du serveur (`ArticleFacet`) ; ce qui manque ici est un champ sans facette, pas une erreur.
 */
export const FACET_FIELD: Readonly<Record<ArticleFacet, VocabularyFieldKey>> = {
  fam: 'family', sfam: 'subFamily', sect: 'sector', sup: 'supplier',
  marq: 'brand', sais: 'season', coll: 'collection', coul: 'color',
};

/** Le mot que CE magasin emploie pour la facette — lu au rendu, comme `fieldLabel`. */
export function facetLabel(facet: ArticleFacet): string {
  return fieldLabel(FACET_FIELD[facet]);
}
